import React, { useRef, useState } from "react";
import { Button, Card, Col, Row, Stack } from "react-bootstrap";
import { useParams } from "react-router-dom";
import {
  banGuardian,
  banMember,
  patchGuardian,
  patchMember,
  postNote,
  postSummary,
} from "../../services/httpClient";
import SelectObject from "./SelectObject";
import Textarea from "./Textarea";

interface BanState {
  user: any;
  previousJob: string;
  isBanned: boolean;
  setIsBanning: any;
  fetchHousehold: any;
  resetCurrentView: any;
  type: string;
  caseStatus: string;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const ConfirmBan = ({
  user,
  previousJob,
  isBanned,
  setIsBanning,
  fetchHousehold,
  resetCurrentView,
  type,
  caseStatus,
  setShowToast,
  setToastText,
  setToastBody,
}: BanState) => {
  const { id } = useParams();
  const [banReason, setBanReason] = useState<string>("");
  const [duration, setDuration] = useState<string>("indefinite");
  const [allowBan, setAllowBan] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const progress = useRef<{
    banned: boolean;
    noteId?: number | string | null;
    patched: boolean;
  }>({ banned: false, patched: false });
  const name = user?.firstname + " " + user?.lastname;

  const durationOptions = [
    { key: "30", name: "30 Day" },
    { key: "60", name: "60 Day" },
    { key: "90", name: "90 Day" },
    { key: "180", name: "180 Day" },
    { key: "365", name: "1 Year" },
    { key: "indefinite", name: "Indefinite" },
  ];

  const updateBanReason = (e: any) => {
    setBanReason(e.target.value);
    setAllowBan(true);
  };

  const updateBanDuration = (e: any) => {
    setDuration(e.target.value);
  };

  const onClickBanConfirm = async () => {
    if (isSaving) return;

    const isGuardian = type.toLowerCase() === "guardian";
    const subjectType = isGuardian ? "guardian" : "member";
    const noun = isGuardian ? "Guardian" : "Member";
    const banFn = isGuardian ? banGuardian : banMember;
    const patchFn = isGuardian ? patchGuardian : patchMember;
    const banning = !isBanned;
    const inactive = caseStatus === "inactive";
    const today = new Date().toISOString().split("T")[0];

    // inactive cases only get a standard note; otherwise ban -> exit note, unban -> intake note
    const note = inactive
      ? {
          notetype: "standard",
          subject: { id: user?.id, type: subjectType },
          fields: { text: banReason, date: today },
        }
      : banning
        ? {
            notetype: "exit",
            subject: { id: user?.id, type: subjectType },
            fields: { exit_type: "ban", description: banReason, date: today },
          }
        : {
            notetype: "intake",
            subject: { id: user?.id, type: subjectType },
            fields: { description: banReason, date: today },
          };

    setErrorMsg("");
    setIsSaving(true);

    // When banning, the ban goes first so the status that matters is never left unapplied.
    // Steps that already succeeded are remembered so a retry does not duplicate the note.
    let step = "ban";
    try {
      if (banning && !progress.current.banned) {
        await banFn({ is_banned: true, duration: duration }, id, user?.id);
        progress.current.banned = true;
      }

      step = "note";
      if (progress.current.noteId === undefined) {
        const res: any = await postNote(note, id);
        const noteId = res[0].data.note_id;
        if (!inactive && !noteId) throw new Error("Note was not saved");
        progress.current.noteId = noteId ?? null;
      }

      if (!inactive && !progress.current.patched) {
        step = "dates";
        const noteId = progress.current.noteId;
        const data = banning
          ? { exit_at: today, exit_note_id: noteId }
          : {
              intake_at: today,
              intake_note_id: noteId,
              exit_at: null,
              exit_note_id: null,
            };
        await patchFn(data, id, user?.id);
        progress.current.patched = true;
      }

      if (!banning) {
        step = "ban";
        await banFn({ is_banned: false, duration: duration }, id, user?.id);
      }

      //create new ai summary, failures here are not worth blocking on
      postSummary(id).catch((err: any) => console.log(err));

      resetCurrentView();
      fetchHousehold();
      setShowToast(true);
      setToastText(`${noun} ${banning ? "Banned" : "Unbanned"}`);
      setToastBody(name);
      setIsBanning(false);
    } catch (err: any) {
      console.log(err);
      const what =
        step === "ban"
          ? "update the ban status"
          : step === "note"
            ? "save the note"
            : "update the exit/intake dates";
      const reason = err?.response?.data?.msg;

      if (progress.current.banned) {
        // the ban is already in effect, so refresh and close, but tell the user what is missing
        postSummary(id).catch((e: any) => console.log(e));
        resetCurrentView();
        fetchHousehold();
        setShowToast(true);
        setToastText(`${noun} Banned, but follow-up failed`);
        setToastBody(
          `${name}: could not ${what}. Please add the details manually.`,
        );
        setIsBanning(false);
      } else {
        setErrorMsg(
          `Unable to ${what}. ${reason ?? "Please check your connection and try again."}`,
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  const onClickCancelBan = () => {
    setIsBanning(false);
    setAllowBan(false);
  };

  return (
    <Card className="bg-light mb-3">
      <Card.Body>
        <Row>
          <Col xs={12}>
            <Stack direction="horizontal" className="mb-3">
              <Col xs={6}>
                <Card.Subtitle>
                  Are you sure you want to {isBanned ? "unban" : "ban"} {name}?
                </Card.Subtitle>
              </Col>
              <Col xs={6} className="d-flex justify-content-end">
                <Stack direction="horizontal" gap={3}>
                  <Button
                    className="app-danger-color button"
                    onClick={() => onClickBanConfirm()}
                    disabled={!allowBan || isSaving}
                  >
                    Confirm {isBanned ? "Unban" : "Ban"}
                  </Button>
                  <div className="vr" />
                  <Button
                    className="app-primary-color button"
                    onClick={() => onClickCancelBan()}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Col>
            </Stack>
          </Col>
        </Row>
        {errorMsg && (
          <Row>
            <Col xs={12}>
              <p className="errmsg" role="alert">
                {errorMsg}
              </p>
            </Col>
          </Row>
        )}
        <Row>
          <Col xs={8}>
            <Textarea
              label={isBanned ? "Enter Unban Reason" : "Enter Ban Reason"}
              value={banReason}
              hasFocus={true}
              rows={1}
              onUpdate={(e: any) => updateBanReason(e)}
              css={""}
            />
          </Col>
          <Col xs={4}>
            {!isBanned && (
              <SelectObject
                label="Duration"
                value={duration}
                options={durationOptions}
                onUpdate={(e: any) => updateBanDuration(e)}
                skipInitialOption={true}
              />
            )}
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default ConfirmBan;
